import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Download, Trash2, ShieldCheck, FileText, Loader2 } from "lucide-react";

export default function MyDataPage() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const exportData = async () => {
    setBusy(true);
    try {
      const r = await api.get("/gdpr/my-data");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `i-miei-dati-verixa-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export completato (formato JSON portatile · art. 20 GDPR)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async () => {
    const phrase = window.prompt(
      "⚠️ CANCELLAZIONE ACCOUNT (Art. 17 GDPR)\n\n" +
      "Stai per cancellare definitivamente il tuo account e tutti i dati personali associati.\n" +
      "Alcuni record con obbligo legale di conservazione (es. cedolini, fatture) resteranno in forma pseudonimizzata.\n\n" +
      "L'operazione è IRREVERSIBILE.\n\nDigita CANCELLAMI per confermare:",
      ""
    );
    if (phrase !== "CANCELLAMI") {
      if (phrase !== null) toast.error("Conferma annullata");
      return;
    }
    setBusy(true);
    try {
      await api.delete("/gdpr/my-data");
      toast.success("Account cancellato. Sarai disconnesso.");
      localStorage.removeItem("token");
      setTimeout(() => nav("/"), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      setBusy(false);
    }
  };

  const dpaPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get("/gdpr/dpa-template", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DPA_Verixa.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Solo per admin");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-12">Caricamento…</div>;

  return (
    <div className="p-8 md:p-12 max-w-4xl" data-testid="my-data-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Privacy · GDPR</div>
        <h1 className="font-display text-4xl">I miei dati</h1>
        <p className="text-[#555] mt-1 text-sm">Esercita i tuoi diritti ai sensi del Regolamento UE 2016/679 (GDPR).</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Export */}
        <div className="border-2 border-black rounded-3xl bg-white p-6">
          <div className="w-12 h-12 rounded-xl bg-[#1E2347] flex items-center justify-center mb-3">
            <Download className="w-6 h-6 text-[#3B82F6]"/>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#3B82F6] font-bold">Art. 15 · 20</div>
          <h2 className="font-display text-2xl mt-1 mb-2">Esporta i miei dati</h2>
          <p className="text-sm text-[#555] mb-4">Scarica un file JSON con tutti i dati personali che trattiamo su di te: profilo, timbrature, ferie, rimborsi, documenti.</p>
          <Button onClick={exportData} disabled={busy} data-testid="export-my-data-btn" className="rounded-full bg-[#3B82F6] hover:bg-black h-11">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Download className="w-4 h-4 mr-2"/>}
            Scarica JSON
          </Button>
        </div>

        {/* Cancellazione */}
        <div className="border-2 border-red-300 rounded-3xl bg-red-50/40 p-6">
          <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-3">
            <Trash2 className="w-6 h-6 text-red-600"/>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-bold">Art. 17 · diritto all'oblio</div>
          <h2 className="font-display text-2xl mt-1 mb-2">Cancella account</h2>
          <p className="text-sm text-[#555] mb-4">
            Elimina account e dati personali. I record con obbligo legale (es. cedolini, fatture) restano pseudonimizzati.
          </p>
          <Button onClick={deleteAccount} disabled={busy} data-testid="delete-my-account-btn" variant="outline" className="rounded-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white h-11">
            <Trash2 className="w-4 h-4 mr-2"/> Cancella account
          </Button>
        </div>

        {/* DPA solo admin */}
        {me?.role === "admin" && (
          <div className="border-2 border-black rounded-3xl bg-white p-6 md:col-span-2">
            <div className="w-12 h-12 rounded-xl bg-[#1E2347] flex items-center justify-center mb-3">
              <FileText className="w-6 h-6 text-[#3B82F6]"/>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#3B82F6] font-bold">Art. 28 · per aziende</div>
            <h2 className="font-display text-2xl mt-1 mb-2">DPA · Accordo trattamento dati</h2>
            <p className="text-sm text-[#555] mb-4">
              Scarica il Data Processing Agreement precompilato con i dati della tua azienda. Da firmare congiuntamente con Verixa per conformità all'art. 28 GDPR.
            </p>
            <Button onClick={dpaPdf} disabled={busy} data-testid="dpa-download-btn" className="rounded-full bg-black hover:bg-[#3B82F6] h-11 text-white">
              <FileText className="w-4 h-4 mr-2"/> Scarica DPA (PDF)
            </Button>
          </div>
        )}
      </div>

      <div className="mt-10 bg-[#1E2347] border border-[#3B82F6]/30 rounded-2xl p-6 flex gap-4">
        <ShieldCheck className="w-8 h-8 text-[#3B82F6] flex-shrink-0"/>
        <div className="text-sm text-[#333]">
          <strong>Altri diritti GDPR</strong>: per esercitare rettifica, opposizione o limitazione del trattamento, oppure per presentare reclamo, scrivi a <a href="mailto:privacy@verixa.it" className="text-[#3B82F6] font-semibold">privacy@verixa.it</a>. Hai anche il diritto di reclamo presso il Garante della Privacy <a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-[#3B82F6] font-semibold">www.garanteprivacy.it</a>.
        </div>
      </div>
    </div>
  );
}
